import { Repo } from '../data/schema.js';
import { getItem } from '../data/items.js';
import { Tags } from './tags.js';
import { Inspire } from './inspire.js';
import {
  STAGES, STAGE_COUNT, BASE_FAIL, BENCHES, BENCH_PROPS, SCENE_BENCH,
  MACHINERY_FAIL_PER_POINT, MACHINERY_FAIL_CAP, DEFAULT_VEHICLE_KIND, workSeconds,
} from '../data/vehicles.js';

/**
 * 攒车：一辆车要抡五遍扳手（底盘 / 动力 / 转向 / 电气 / 总装）。
 *
 * 规矩：
 *   - 开工一次只出一辆，攒到一半不会丢（存在 db.vehicles 里）
 *   - 缺料就把缺的报出来，半颗螺栓也不糊弄
 *   - 会失手：失败只糟践辅料，大件（车架、引擎、轮胎、电瓶、座椅）一个不毁
 *   - 失手率 = 基础 22% − 工位加成 − 机械手艺，前期落在 15%~25%
 */

function table(db) { return db.table('vehicles'); }

function clamp(v, a = 0, b = 100) { return Math.max(a, Math.min(b, v)); }

/** 干活消耗的游戏时间：只推进钟点，跨日不另结算（那些活儿在天亮那一档统一走） */
function spendTime(db, minutes) {
  const w = Repo.getWorld(db);
  const total = w.minute + minutes;
  const dayAdd = Math.floor(total / 1440);
  Repo.saveWorld(db, {
    minute: total % 1440,
    day: w.day + dayAdd,
    dayCount: w.dayCount + dayAdd,
  });
}

function itemName(id) { return getItem(id).name; }

export const Assemble = {
  /** 这一手工位怎么样：看场景，也看你站在哪件家伙跟前 */
  benchOf(scene = '', propType = '') {
    if (propType && BENCH_PROPS[propType]) return BENCH_PROPS[propType];
    return SCENE_BENCH[scene] || 'none';
  },

  bench(id) { return BENCHES[id] || BENCHES.none; },

  /** 失手率（0~1），UI 与开工都得能问；作场那张照也能少失手 */
  failRate(db, benchId = 'none') {
    const b = this.bench(benchId);
    const skill = Tags.get(db, 'machinery').total;
    const craft = Inspire.perks(db, 'craft').craftOff || 0;
    const cut = b.bonus + Math.min(MACHINERY_FAIL_CAP, skill * MACHINERY_FAIL_PER_POINT) + craft;
    return Math.max(0.02, BASE_FAIL - cut);
  },

  /** 在施工的那辆（stage 没走完的） */
  current(db) {
    return table(db).first((r) => Number(r.stage) < STAGE_COUNT) || null;
  },

  /** 已经能跑的车，可能不止一辆（升级不过是换档，不另起一行） */
  cars(db) {
    return table(db).where((r) => Number(r.stage) >= STAGE_COUNT);
  },

  firstCar(db) { return this.cars(db)[0] || null; },

  /** 缺什么：返回「轮胎(2/4)」这样的短句 */
  missing(db, stageIndex) {
    const need = STAGES[stageIndex] ? STAGES[stageIndex].need : {};
    const out = [];
    for (const [id, n] of Object.entries(need)) {
      const has = Repo.countItem(db, id);
      if (has < n) out.push(`${itemName(id)}(${has}/${n})`);
    }
    return out;
  },

  /** 开工：架起一副车架子，从此按五段往上安装 */
  begin(db, opts = {}) {
    if (this.current(db)) return { ok: false, reason: '架子已经支上了，接着往下装。' };
    const p = Repo.getPlayer(db);
    const row = table(db).insert({
      kind: DEFAULT_VEHICLE_KIND,
      name: '还没攒完的车',
      scene: opts.scene || p.scene || 'carport',
      gx: opts.gx || 0, gy: opts.gy || 0,
      stage: 0,
      fuel: 0, durability: 0,
      quality: 0, attempts: 0, odo: 0,
      broken: false,
    });
    Tags.gain(db, 'machinery', 1, 'assemble');
    return { ok: true, id: row.id, text: '把车架子支上了，第一遍扳手是底盘。' };
  },

  /**
   * 开工前的盘算：这一段能不能拧、要拧多久条。
   * 面板靠它决定「能不能点」，读条靠它定进度条长度；真正结算还是 install。
   */
  planWork(db, opts = {}) {
    const row = this.current(db);
    if (!row) return { ok: false, reason: '还没支上架子。' };
    const idx = Number(row.stage) || 0;
    const stage = STAGES[idx];
    if (!stage) return { ok: false, reason: '车子已经攒完了。' };

    const lack = this.missing(db, idx);
    if (lack.length) return { ok: false, reason: `还缺：${lack.join('、')}` };

    const p = Repo.getPlayer(db);
    if ((p.energy || 0) < 12) return { ok: false, reason: '手都抬不动了，先歇一夜。' };

    const benchId = opts.bench || 'none';
    const skill = Tags.get(db, 'machinery').total;
    return {
      ok: true, row, index: idx, stage, benchId, rate: this.failRate(db, benchId),
      sec: workSeconds(idx, { benchId, skill }),
    };
  },

  /** 装上一段。prop 用来判工位（站在铁砧前就是铁砧的水平） */
  install(db, opts = {}) {
    const plan = this.planWork(db, opts);
    if (!plan.ok) return { ok: false, reason: plan.reason };
    const row = plan.row;
    const idx = plan.index;
    const stage = plan.stage;

    for (const [id, n] of Object.entries(stage.need)) Repo.takeItem(db, id, n);

    const p = Repo.getPlayer(db);
    const bench = this.bench(plan.benchId);
    const fail = plan.rate;
    const missed = Math.random() < fail;

    Repo.savePlayer(db, {
      energy: clamp(p.energy - 12),
      fatigue: clamp((p.fatigue || 0) + 3, 0, 120),
      mood: clamp(p.mood - 1),
    });
    spendTime(db, stage.min);
    Tags.gain(db, 'machinery', stage.tagEff, 'assemble');
    table(db).update(row.id, { attempts: (row.attempts || 0) + 1 });

    if (missed) {
      const lost = [];
      for (const id of stage.soft) {
        if (Math.random() < 0.55 && Repo.countItem(db, id) > 0) {
          Repo.takeItem(db, id, 1);
          lost.push(itemName(id));
        }
      }
      const tail = lost.length ? `，糟践了${lost.join('、')}` : '，好在没搭进去零件';
      return {
        ok: true, failed: true, bench: bench.name, rate: fail,
        text: `${stage.name}这一段没成${tail}。`,
      };
    }

    const next = idx + 1;
    const patch = {
      stage: next,
      quality: Math.min(100, (row.quality || 0) + 12 + Math.round(bench.bonus * 100)),
    };
    if (next >= STAGE_COUNT) {
      // 出车：落在这个场景里能站住的一格
      const spot = this.parkSpot(db, opts);
      patch.name = '攒出来的车';
      patch.scene = spot.scene; patch.gx = spot.gx; patch.gy = spot.gy;
      patch.durability = 60; patch.maxDur = 60; patch.fuel = 0;
    }
    table(db).update(row.id, patch);
    Tags.gain(db, 'skillful', 1, 'assemble', 10);   // 手巧是拧出来的
    return {
      ok: true, failed: false, bench: bench.name, rate: fail,
      stage: next, finished: next >= STAGE_COUNT,
      text: next >= STAGE_COUNT ? `${stage.done}车能开会走的那一刻，手是抖的。` : stage.done,
    };
  },

  /** 出车落点：玩家脚边优先，找不到就退回车棚 */
  parkSpot(db, opts = {}) {
    const p = Repo.getPlayer(db);
    const scene = opts.scene || p.scene || 'carport';
    return { scene, gx: Math.round(p.x / 40) + 1, gy: Math.round(p.y / 40) };
  },

  /** 攒到一半想推倒重来：退回一部分大件 */
  scrap(db) {
    const row = this.current(db);
    if (!row) return { ok: false, reason: '没有正在攒的车。' };
    const back = {};
    for (let i = 0; i < Number(row.stage) || 0; i++) {
      for (const [id, n] of Object.entries(STAGES[i].need)) {
        // 辅料就别指望全回来了
        const rate = ['plate_scrap', 'wood_scrap', 'bolt', 'fuel'].includes(id) ? 0.5 : 1;
        back[id] = (back[id] || 0) + Math.max(0, Math.floor(n * rate));
      }
    }
    table(db).remove(row.id);
    for (const [id, n] of Object.entries(back)) if (n > 0) Repo.addItem(db, id, n);
    const names = Object.entries(back).filter(([, n]) => n > 0)
      .map(([id, n]) => `${itemName(id)}×${n}`);
    return { ok: true, text: names.length ? `拆了架在半道的车，退回${names.join('、')}。` : '拆了个干净。' };
  },

  /** 一句话概括当前进度，给 UI 标题用 */
  summary(db) {
    const row = this.current(db);
    if (row) return { state: 'building', stage: Number(row.stage) || 0, total: STAGE_COUNT };
    const car = this.firstCar(db);
    if (car) return { state: 'done', car };
    return { state: 'none', stage: 0, total: STAGE_COUNT };
  },
};
